import '~styles/index.scss';
import 'react-toastify/dist/ReactToastify.css';

import { FullStory, init as initFullStory } from '@fullstory/browser';
import { alertClasses, paperClasses } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Subscription } from '@rails/actioncable';
import { LoadScript } from '@react-google-maps/api';
import * as Sentry from '@sentry/react';
import {
  Deployment_Read,
  DeploymentServiceName,
  DeploymentState,
} from '@treadinc/horizon-api-spec';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { MessagePayload } from 'firebase/messaging';
import { onMessage } from 'firebase/messaging';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useMatches } from 'react-router-dom';
import { toast } from 'react-toastify';

import BillingStatusBanner from '~components/BillingStatusBanner/BillingStatusBanner';
import CompanyOnboardingBanner from '~components/CompanyOnboardingBanner/CompanyOnboardingBanner';
import CustomToastContent from '~components/CustomToast/CustomToastContent';
import Dialog from '~components/Dialog/Dialog';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { syncIntercomUser } from '~components/Intercom/intercom-utils';
import { GOOGLE_MAPS_LOADER_OPTIONS } from '~components/Maps/v2/constants';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { APPLE_STORE_LINK, PLAY_STORE_LINK } from '~constants/consts';
import { FeatureFlags } from '~constants/featureFlags';
import { BulkSendProgressContextProvider } from '~contexts/BulkSendProgressContext';
import { FileDownloadResultContextProvider } from '~contexts/FileDownloadResultContext';
import { OutboundEmailProgressContextProvider } from '~contexts/OutboundEmailProgressContext';
import useHydrateThemeFromUser from '~contexts/ThemeMode/useHydrateThemeFromUser';
import useSecondsOnPageTracker from '~hooks/useAnalytics/useSecondsOnPageTracker';
import useMessagingNewMessageToast from '~hooks/useMessaging/useMessagingNewMessageToast';
import useMessagingRealTimeUpdates from '~hooks/useMessaging/useMessagingRealTimeUpdates';
import { User } from '~hooks/useUsers/models';
import useUsers from '~hooks/useUsers/useUsers';
import AppleBadge from '~images/apple-badge.svg?react';
import googlePlayBadgeSrc from '~images/google-play-badge.png';
import { generateToken, messaging } from '~notifications/firebase';
import { deepestRouteHandle } from '~router/routeHandle';
import { realTimeChannels } from '~services/realTimeChannels';
import { isImpersonating } from '~services/sessionTokens';
import notificationSound from '~sounds/notification.wav';
import { useStores } from '~store';
import TreadAgentDock from '~tread-agent/TreadAgentDock';
import { alert } from '~types/AlertTypes';
import { useAwaitableFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { isMobileBrowser } from '~utils/isMobileBrowser';
import { isProductionEnv } from '~utils/isProductionEnv';
import { subscribeToChannel } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import AppHeader from './AppHeader/AppHeader';
import ImpersonationPill from './AppHeader/ImpersonationPill';
import ImpersonationFrame from './ImpersonationFrame';

const configureReporters = (currentUser: User) => {
  if (isProductionEnv()) {
    // Intialize FullStory identity for the current user
    initFullStory({ orgId: 'o-1S518Y-na1' }, () => {
      FullStory('setIdentity', {
        uid: currentUser.id,
        properties: {
          displayName: `${currentUser.firstName} ${currentUser.lastName}`,
          email: currentUser.email,
        },
      });
    });

    syncIntercomUser(currentUser);
  }

  // Sentry user tracking
  Sentry.setUser({
    id: currentUser.id,
    ...(currentUser.email ? { email: currentUser.email } : {}),
    company_id: currentUser?.company?.id,
    company_name: currentUser?.company?.legalName,
    ip_address: '{{auto}}',
  });
  // have to use setTag so we can search for it in sentry, see https://sentry.zendesk.com/hc/en-us/articles/26861002545691-How-to-search-events-on-context-or-extra-data
  Sentry.setTag('company_id', currentUser?.company?.id);
};

const RouteHeader = () => {
  const { header: Header } = deepestRouteHandle(useMatches());

  return Header ? <Header /> : null;
};

const MainContent = ({ children }: { children: ReactNode }) => {
  const { isFullBleed } = deepestRouteHandle(useMatches());

  return (
    <Box
      component="main"
      sx={{ px: isFullBleed ? 0 : 5 }}
      className={classNames({ loading: false })}
    >
      {children}
    </Box>
  );
};

const AuthenticatedLayout = () => {
  const [isMobileWarningOpen, setIsMobileWarningOpen] = useState(isMobileBrowser());
  const [appUpdateSubscription, setAppUpdateSubscription] = useState<Subscription>();

  const handleMobileWarningClose = useCallback(
    () => setIsMobileWarningOpen(false),
    [setIsMobileWarningOpen],
  );

  const { toasterStore, userStore } = useStores();

  // Adopt the user's saved theme once their record loads (cross-device sync).
  useHydrateThemeFromUser();
  useSecondsOnPageTracker();

  const inAppMessagingFeatureFlag = useAwaitableFeatureFlag({
    featureFlagKey: FeatureFlags.inAppMessaging,
  });

  const isInAppMessagingEnabled =
    inAppMessagingFeatureFlag.isReady && inAppMessagingFeatureFlag.isFeatureFlagEnabled;

  const notificationAlertSound = useMemo(
    () => new Audio(notificationSound),
    [notificationSound],
  );

  const { toastMessage, toastPush } = useMessagingNewMessageToast({
    alertSound: notificationAlertSound,
  });

  // Subscribed here, not on the Messages page, so unopened conversations stay fresh. A viewer
  // with messaging off still subscribes, for the Dispatch conversations of owners who have it on.
  useMessagingRealTimeUpdates({
    companyId: getEffectiveUserCompanyId(userStore),
    onMessageCreated: isInAppMessagingEnabled ? toastMessage : undefined,
  });

  const { addUserDeviceToken } = useUsers();

  useEffect(() => {
    // Get the timezone from the user's model
    if (userStore?.user?.timeZone) {
      dayjs.tz.setDefault(userStore.user.timeZone);
    }
  }, [userStore.user?.timeZone]);

  useEffect(() => {
    if (userStore.user.id && !isImpersonating()) {
      const currentUser = userStore.user;
      configureReporters(currentUser);
    } else {
      Sentry.setUser({
        ip_address: '{{auto}}',
      });
    }
  }, [userStore.user.id]);

  useEffect(() => {
    if (!appUpdateSubscription) {
      subscribeToChannel(
        realTimeChannels.DeploymentChannel,
        null,
        setAppUpdateSubscription,
        (response: { data: Deployment_Read }) => {
          if (
            response.data.service === DeploymentServiceName.HORIZON_FE &&
            response.data.state === DeploymentState.SUCCESS
          ) {
            const Message = (
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Typography sx={{ fontSize: '12px' }}>
                  <Typography component="span" sx={{ fontSize: 'inherit' }}>
                    {t('app_update.message_line_1')}
                  </Typography>
                  <br />
                  <Typography component="span" sx={{ fontSize: 'inherit' }}>
                    {t('app_update.message_line_2')}
                  </Typography>
                </Typography>

                <SmallButton color="brandV2Blue2" size="small" variant="contained">
                  {t('app_update.reload_cta')}
                </SmallButton>
              </Box>
            );

            toasterStore.push(
              alert(
                Message,
                'info',
                undefined,
                () => window.location.reload(),
                true,
                {
                  [`& .${paperClasses.root}`]: {
                    boxShadow: 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px',
                  },
                  [`& .${alertClasses.icon}`]: { display: 'none' },
                },
                undefined,
                true,
              ),
              true,
            );
          }
        },
      );
    }
    return () => {
      appUpdateSubscription?.unsubscribe();
    };
  }, [appUpdateSubscription, setAppUpdateSubscription]);

  const fetchUserAndGenerateToken = useCallback(async () => {
    try {
      if (
        navigator.userAgent.indexOf('Firefox') !== -1 ||
        (navigator.userAgent.indexOf('Safari') !== -1 &&
          navigator.userAgent.indexOf('Chrome') === -1)
      ) {
        document.addEventListener(
          'click',
          async () => {
            const token = await generateToken();
            if (token) {
              await addUserDeviceToken(token);
            }
          },
          { once: true },
        );
      } else {
        const token = await generateToken();
        if (token) {
          await addUserDeviceToken(token);
        }
      }
    } catch (error) {
      console.error('An error occurred during user fetching or token generation:', error);
    }
  }, []);

  useEffect(() => {
    // Call this function only if the user is on a desktop browser.
    if (!isMobileBrowser()) {
      fetchUserAndGenerateToken();
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onMessage(messaging, (payload: MessagePayload) => {
      if (isInAppMessagingEnabled && toastPush(payload)) return;

      if (payload.notification) {
        toast(
          <CustomToastContent
            title={payload.notification.title ?? ''}
            body={payload.notification.body ?? ''}
          />,
          {
            position: 'top-right',
            autoClose: 60000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          },
        );
        notificationAlertSound.play().catch(console.error);
      }
    });

    return () => unsubscribe();
  }, [isInAppMessagingEnabled, notificationAlertSound, toastPush]);

  return (
    <>
      {isImpersonating() && <ImpersonationFrame />}

      <div className="wrapper">
        <AppHeader />
        <RouteHeader />
        {isImpersonating() && (
          <Box
            sx={(theme) => ({
              display: 'flex',
              justifyContent: 'center',
              px: 3,
              py: 1,
              backgroundColor: theme.semantic.chipBrandBg,
              borderBottom: `1px solid ${theme.semantic.elevatedStateBorder}`,
            })}
          >
            <ImpersonationPill />
          </Box>
        )}
        <BillingStatusBanner />
        <CompanyOnboardingBanner />
        <MainContent>
          <LoadScript {...GOOGLE_MAPS_LOADER_OPTIONS}>
            <FileDownloadResultContextProvider>
              <BulkSendProgressContextProvider>
                <OutboundEmailProgressContextProvider>
                  <Outlet />
                </OutboundEmailProgressContextProvider>
              </BulkSendProgressContextProvider>
            </FileDownloadResultContextProvider>
          </LoadScript>
        </MainContent>
      </div>

      <TreadAgentDock />

      <Dialog isOpen={isMobileWarningOpen} onClose={handleMobileWarningClose}>
        <DialogHeader onClose={handleMobileWarningClose}>
          {t('mobile_dialog.title')}
        </DialogHeader>

        <DialogContent>
          <Typography variant="body1">{t('mobile_dialog.message')}</Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <Box
              aria-label={t('mobile_dialog.apple_store_alt_text')}
              component="a"
              href={APPLE_STORE_LINK}
              rel="noreferrer"
              target="_blank"
            >
              <AppleBadge style={{ width: '110px' }} />
            </Box>

            <Box
              component="a"
              href={PLAY_STORE_LINK}
              rel="noreferrer"
              target="_blank"
              sx={{ width: '140px', ml: 0 }}
            >
              <Box
                component="img"
                alt={t('mobile_dialog.google_play_alt_text') || ''}
                src={googlePlayBadgeSrc}
                style={{ width: '180px' }}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default observer(AuthenticatedLayout);
